import { Ajaxinate } from './ajaxinate.js';

/**
 * https://community.shopify.com/c/shopify-apps/code-splitting-modular-of-javascript-file-inside-theme-app/m-p/2062857/highlight/true#M63011
 * note we are importing the ESM file, not the dist package:
 * https://raw.githubusercontent.com/Elkfox/Ajaxinate/master/src/ajaxinate.js
 *
 * https://github.com/Elkfox/Ajaxinate
 * https://ajaxinate.elkfox.io
 *
 * note re Shopify and ESM: we can get away with doing this via ESM because we want the entirety of the Ajaxinate script
 * to be evaluated on page load, and given that we're not trying to load it elsewhere using an asset_url filter, this
 * means it'll only be loaded and evaluated once.
 *
 * trying to do ESM for real in Shopify is impossible or really problematic at present because loading a module and later
 * importing some of its methods guarantees that the script will be loaded and evaluated twice; once with a cachebuster param
 * and once without. (this will only happen in Shopify itself, not in a local environment or when using shopify-cli.)
 *
 * https://github.com/Shopify/dawn/issues/316
 */

const ajaxinateContainerSelector = '[data-ajaxinate]';
const ajaxinateNextPaginationLinkContainerSelector = '[data-ajaxinate-pagination-next]';
const itemPageSelector = '[data-itempage]';

/**
 * find what 'page' the item clicked is on, then push history so that when the user
 * returns to the collection, they are taken back to the same page
 * @param {Event} event
 */
function historicizeItemPage(event) {
	const itemPageEl = event.target.closest(itemPageSelector);
	if (!itemPageEl) return;
	const url = new URL(location);
	const page = itemPageEl.dataset.itempage;
	url.searchParams.set('page', page);
	window.history.scrollRestoration = 'manual';
	window.history.replaceState(null, '', url);
}

/**
 * initialize and add historicize listener (which we can do repeatedly without fear of
 * duplicating because they're the same function), either on page load or after filtering
 */
function ajaxinate() {
	const ajaxinateContainer = document.querySelector(ajaxinateContainerSelector);
	ajaxinateContainer.addEventListener('click', historicizeItemPage, { once: true });
	if (window?.ajaxination) window.ajaxination.destroy();
	window.ajaxination = new Ajaxinate({
		container: ajaxinateContainerSelector,
		pagination: ajaxinateNextPaginationLinkContainerSelector,
		method: 'scroll',
		offset: 300,
		callback: () => {
			if (window.location.href.includes('/collections/')) {
				window.injectSecondaryImages();
			}
		}
	});
}

/**
 * once collection filters have been applied, our original ajaxinate instance can't work, because
 * what it was targeting in the DOM has been destroyed and recreated – so we need to reinitialize,
 * and in order to know when to do that, we need to watch for changes filters make to the DOM
 */
function maybeWatchForFiltering() {
	/** FacetFiltersForm.renderProductCount() will change this element's innerHTML */
	const renderProductCountContainer = document.getElementById('ProductCount');
	if (renderProductCountContainer) {
		let observer = new window.MutationObserver(ajaxinate);
		observer.observe(renderProductCountContainer, { childList: true });
	}
}

/**
 * initialize when we have the necessary elements
 */
function maybeAjaxinate() {
	const ajaxinateContainer = document.querySelector(ajaxinateContainerSelector);
	const ajaxinateNextPaginationLinkContainer = document.querySelector(ajaxinateNextPaginationLinkContainerSelector);
	if (!ajaxinateContainer || !ajaxinateNextPaginationLinkContainer) return;
	ajaxinate();
	maybeWatchForFiltering();
}

window.addEventListener('DOMContentLoaded', maybeAjaxinate, { once: true });
